import { useQuery } from "react-query";
import { genericError } from "./errors";
import { useState } from "react";

const buildPath = (path, params) => {
  if (!params || !Object.keys(params).length) return path;
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) =>
    Array.isArray(v) ? v.forEach((x) => sp.append(`${k}[]`, x)) : sp.append(k, String(v))
  );
  return `${path}?${sp}`;
};

export const useApiGet = (path: string, params = {}, config = {}) => {
  const q = useQuery(
    [path, params],
    () =>
      fetch(buildPath(path, params), {
        headers: { Accept: "application/json" },
      }).then((res) => res.json()),
    {
      keepPreviousData: true,
      ...config,
    }
  );

  let unavailable;
  let data = q.data;

  if (q.isFetching && !q.isFetched) {
    unavailable = <div></div>;
  } else if (q.error) {
    unavailable = <div>There was an error</div>;
    genericError();
  }

  return {
    data,
    unavailable,
    loading: q.isFetching,
    loaded: q.isSuccess,
    refetch: q.refetch,
  };
};

export const usePageData = () => {
  return useApiGet(location.pathname + location.search);
};

const csrfToken = document
  .querySelector('meta[name="csrf-token"]')
  ?.getAttribute("content");

export const useApiMutation = () => {
  let callback;

  const [saving, setSaving] = useState(false);

  const makeRequest =
    (method: "PUT" | "POST" | "DELETE") =>
    async (path: string, data = {}, config = {}) => {
      setSaving(true);
      try {
        const isFormData = data instanceof FormData;
        const response = await fetch(path, {
          method,
          body: isFormData ? data : JSON.stringify(data),
          ...config,
          headers: {
            ...(isFormData ? {} : { "Content-Type": "application/json" }),
            "X-CSRF-Token": csrfToken,
            Accept: "application/json",
            ...(config.headers || {}),
          },
        });
        const responseData = await response.json();
        callback?.(responseData);
        return { data: responseData };
      } catch (error) {
        genericError();
      } finally {
        setSaving(false);
      }
    };

  const put = makeRequest("PUT");
  const post = makeRequest("POST");
  const destroy = makeRequest("DELETE");

  return { post, put, destroy, saving };
};

export const onSuccess = (callback) => (response) => {
  if (response.data.success) {
    callback(response.data);
  } else {
    genericError();
  }
};
